import React, { useState, useEffect } from 'react';

interface HeroCarouselProps {
  images: string[];
  autoRotateInterval?: number;
  /**
   * Height for the image container. Can be a string (e.g. '300px', '40vh') or a responsive Tailwind class string (e.g. 'max-h-[200px] sm:max-h-[280px]')
   */
  height?: string;
}

export default function HeroCarousel({ images, autoRotateInterval = 3000, height }: HeroCarouselProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const imageCount = images?.length ?? 0;

  // Auto-rotate carousel
  useEffect(() => {
    if (imageCount <= 1) {
      return;
    }

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % imageCount);
    }, autoRotateInterval);

    return () => clearInterval(interval);
  }, [imageCount, autoRotateInterval]);

  const activeIndex = imageCount > 0 ? currentSlide % imageCount : 0;

  if (imageCount === 0) {
    return null;
  }

  // Use a stable container height to avoid layout shifts between images with different aspect ratios.
  const normalizedHeight =
    typeof height === 'string' && height.startsWith('[') && height.endsWith(']')
      ? height.slice(1, -1)
      : height;

  const isTailwindHeightClass =
    typeof normalizedHeight === 'string' &&
    (normalizedHeight.startsWith('h-') || normalizedHeight.startsWith('max-h-') || normalizedHeight.startsWith('min-h-'));

  const containerHeightClass = isTailwindHeightClass
    ? normalizedHeight
    : 'h-[180px] sm:h-[220px] md:h-[280px] lg:h-[340px] xl:h-[420px] 2xl:h-[460px]';

  const containerInlineStyle: React.CSSProperties =
    typeof normalizedHeight === 'string' && !isTailwindHeightClass && normalizedHeight
      ? { height: normalizedHeight, maxHeight: '70vh' }
      : {};

  return (
    <section className="relative w-full overflow-hidden m-0 p-0" style={{ display: 'block' }}>
      {/* Carousel Images — rendered as CSS backgrounds so image natural dimensions never affect layout */}
      <div
        className={`relative w-full overflow-hidden ${containerHeightClass}`}
        style={containerInlineStyle}
      >
        {images.map((src, idx) => (
          <div
            key={src}
            aria-label={`Slide ${idx + 1}`}
            role="img"
            style={{
              position: 'absolute',
              inset: 0,
              backgroundImage: `url(${src})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center center',
              backgroundRepeat: 'no-repeat',
              opacity: idx === activeIndex ? 1 : 0,
              transition: 'opacity 0.5s ease-in-out',
            }}
          />
        ))}
      </div>

      {/* Carousel dots (show only if more than 2 images) */}
      {images.length > 2 && (
        <div className="absolute bottom-4 md:bottom-8 left-1/2 transform -translate-x-1/2 flex justify-center gap-2 md:gap-3 z-10">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className="rounded-full transition-all"
              style={{
                width: currentSlide === index ? '32px' : '10px',
                height: '10px',
                background: currentSlide === index ? '#6A1786' : '#fff',
                border: 'none',
                cursor: 'pointer'
              }}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </section>
  );
}